/* eslint-disable camelcase */
import { useFormik } from 'formik';
import { get, isEmpty, set, find, each } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Form, FormLabel, InputGroup, Modal, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import formatDate from '../../../../../frontend/components/components/utils/formatDate';
import Button from '../../../../shared/components/Button';
import FormControl from '../../../../shared/components/Form/FormControl';
import Select from '../../../../shared/components/Select';
import Switch from '../../../../shared/components/Switch/Switch';
import axios from '../../../../utils/axiosConfig';
import i18n from '../../../../utils/i18n';
import { useDispatch } from 'react-redux';
import { setOffer } from '../../../../actions/offer.actions';
import ObjectAttribute, { UIElementType, generateOASchema, validateObjAttrValuesInput } from '../../../../shared/components/ObjectAttribute/ObjectAttribute';
import { scrollToHashInDiv } from '../../../../utils/scrolling';

const ADDRESS_FIELDS = [
  'first_name',
  'last_name',
  'address',
  'address_2',
  'city',
  'state',
  'zip',
  'phone',
];

const MAIN_COUNTRIES = ['United States', 'France'];

// Regex from Yup source-code, ** with an optional protocol **.
// https://github.com/jquense/yup/blob/v1.0.0-beta.8/src/string.ts#L20
const urlRegex = /^(((https?):)?\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

export const hasAnsweredAllQuestions = (userOffer, offer) => {
  if (!get(userOffer, 'questions_and_answers') || !offer.questions) return false;

  const isPhoneRequired = offer.rewards.some(reward => reward.phone_number_required);
  const isPaymentRequired = offer.rewards.some(reward => reward.payment_email_required);
  const isAddressRequired = offer.rewards.some(reward => reward.address_required);
  const isFacebookRequired = offer.facebook_oauth_required;

  const allQuestionsDone = offer.questions.every(question => {
    if (Object.hasOwnProperty.call(userOffer.questions_and_answers, question.field)) {
      const answer = userOffer.questions_and_answers[question.field];
      return !!answer;
    }
    return false;
  });

  const allObjectAttributesDone = offer.object_attribute_metadata
    .filter(oa => userOfferSignUpObjectAttribute(oa) || userObjectAttribute(oa))
    .every(oa => {
      if (Object.hasOwnProperty.call(userOffer.object_attribute_values, oa.code)) {
        const answer = userOffer.object_attribute_values[oa.code] || (oa.datatype == 'boolean');
        return !!answer;
      }
      return false;
    });

  return (
    isPhoneRequired ? !!userOffer.questions_and_answers.phone_number : true
  ) && (
      isPaymentRequired ? !!userOffer.questions_and_answers.paypal_email : true
    ) && (
      isAddressRequired ? !!get(userOffer, 'address.address') : true
    ) && (
      isFacebookRequired ? !!get(userOffer, 'facebook_oauth_token') : true
    ) && allQuestionsDone
    && allObjectAttributesDone;
};

export const userOfferSignUpObjectAttribute = (oa) => oa.object_type_form_string === "user_offer" && oa.influencer_ui_view === "sign_up";
export const userObjectAttribute = (oa) => oa.object_type_form_string === "user";

const validateInput = (datatype = 'string', isMultiple = false, ui_element = '', optional = false) => (v = false) => {
  if (optional) return;

  if (isMultiple) {
    if (Array.isArray(v)) {
      return Yup.array().required().validate(v)
        .then(() => null)
        .catch(() => i18n.t('influencers.common.forms.is_required'));
    }
    return Yup.string().required().validate(v)
      .then(() => null)
      .catch(() => i18n.t('influencers.common.forms.is_required'));

  } else if (datatype == 'string') {
    if (ui_element == 'link') {
      return Yup.string().matches(urlRegex).validate(v)
        .then(() => null)
        .catch(() => i18n.t('influencers.common.forms.is_not_valid_url'));
    }
    return Yup.string().required().validate(v)
      .then(() => null)
      .catch(() => i18n.t('influencers.common.forms.is_required'));
  } else if (datatype == 'int') {
    return Yup.number().required().validate(v)
      .then(() => null)
      .catch(() => i18n.t('influencers.common.forms.is_required'));
  } else if (datatype == 'boolean') {
    return Yup.boolean().oneOf([false]).required().validate(v)
      .then(() => null)
      .catch(() => i18n.t('influencers.common.forms.is_required'));
  } else if (datatype == 'datetime') {
    return Yup.date().required().validate(v)
      .then(() => null)
      .catch(() => i18n.t('influencers.common.forms.is_required'));
  }
};

const RequiredInformation = ({
  onRequestClose,
  prevStep,
  nextStep,
  lastStep
}) => {
  const user = useSelector(store => get(store, 'auth.user'));
  const offer = useSelector(store => get(store, 'offer.offer'));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user_offer: userOffer } = offer;
  const { user: influencerData } = userOffer;
  const talentAgents = influencerData && influencerData.talent_agents;
  const { talent_agents: userOfferTalentAgents } = userOffer;
  const oauthSettings = useSelector(store => get(store, 'offer.oAuthSettings'));
  const testFlow = useSelector(store => get(store, 'config.test'));

  const [state, setState] = useState({
    usePrimaryAddress: false,
    updatedForm: Date.now(),
    loading: false,
    isPhoneRequired: false,
    isPaymentRequired: false,
    isAddressRequired: false,
    isFacebookOptional: false,
    isFacebookRequired: false,
    countries: [],
    mainCountries: [],
    hasBeenSubmitted: false,
  });
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const confirmAddressRef = useRef(false);

  const object_attribute_metadata = useMemo(() =>
    get(offer, 'object_attribute_metadata', []).filter(oa => userOfferSignUpObjectAttribute(oa) || userObjectAttribute(oa))
    , [offer.object_attribute_metadata]);

  const validationSchema = Yup.lazy((values) => {
    const address = {
      address: Yup.object({
        first_name: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.first_name')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        last_name: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.last_name')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        address: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.address')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        address_2: Yup.string(),
        city: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.city')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        state: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.state')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        zip: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.zip')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
        country: !values.usePrimaryAddress
          ? Yup.string()
            .required(`${t('influencers.pages.profile.general.country')} ${t('influencers.common.is_required')}`)
          : Yup.string().notRequired(),
      })
    };

    const editableAttrsSchema = generateOASchema(object_attribute_metadata, t);

    return Yup.object({
      usePrimaryAddress: Yup.boolean(),
      questions: Yup.object(),
      connectYoutube: Yup.bool(),
      ...(state.isAddressRequired && address),
      ...editableAttrsSchema
    });
  });

  const editableAttrsValues = useCallback(() => {
    const initialValues = {};
    if (!object_attribute_metadata) return initialValues;

    const objectAttributeValues = get(userOffer, 'object_attribute_values', {});

    object_attribute_metadata.forEach((attr) => {
      const value = get(objectAttributeValues, `${attr.code}`);
      if (
        [UIElementType.CHECKBOXES, UIElementType.DROPDOWN].includes(attr.ui_element)
        && attr.is_multiple
      ) {
        initialValues[attr.code] = (value && value.split(',')) || '';
      } else {
        initialValues[attr.code] = value || '';
      }
    });
    return initialValues;
  }, [object_attribute_metadata]);

  // Gets user_offer_talent_agent linked to talent agent
  const getUserOfferTA = (talentAgentId) => find(userOfferTalentAgents, (uoTA) => uoTA.id == talentAgentId);

  const userOfferTalentAgentsValues = () => {
    const initialValues = {
      user_offer_linked_talent_agents: []
    };

    each(talentAgents, (ta) => {
      if (!isEmpty(getUserOfferTA(ta.id))) initialValues.user_offer_linked_talent_agents.push(ta.id);
    });

    return initialValues;
  };

  const isFacebookOptional = testFlow.includes('fb_optional')
    ? true
    : oauthSettings.facebook.optional || false;
  const isFacebookRequired = testFlow.includes('fb_required')
    ? true
    : oauthSettings.facebook.required || false;
  const showConnectFacebook = isFacebookOptional && !get(userOffer, 'facebook_oauth_token');

  const formik = useFormik({
    initialValues: {
      usePrimaryAddress: state.usePrimaryAddress,
      address: {
        first_name: get(userOffer, 'address.first_name') || '',
        last_name: get(userOffer, 'address.last_name') || '',
        address: get(userOffer, 'address.address') || '',
        address_2: get(userOffer, 'address.address_2') || '',
        city: get(userOffer, 'address.city') || '',
        state: get(userOffer, 'address.state') || '',
        zip: get(userOffer, 'address.zip') || '',
        phone: get(userOffer, 'address.phone') || '',
        country: get(userOffer, 'address.country') || '',
      },
      phone_number: get(userOffer, 'questions_and_answers.phone_number') || get(user, 'phone') || '',
      paypal_email: get(userOffer, 'questions_and_answers.paypal_email') || get(user, 'paypal_email') || '',
      questions: {},
      ...(editableAttrsValues()),
      connectYoutube: false,
      collective_ids: [],
      ...userOfferTalentAgentsValues(),
    },

    validationSchema,
    onSubmit: values => {
      if (testFlow.some(t => ['fb_required', 'fb_optional'].includes(t)))
        return nextStep({
          connectFacebook: true,
        });

      if (testFlow.includes('yt')) return nextStep();

      if (state.hasBeenSubmitted) return nextStep();

      if (showConnectFacebook) return onSaveInfo(values, false);
      onSaveInfo(values, true);
    },
  });

  useEffect(() => {
    axios.get('/api/v1/countries')
      .then(({ data }) => {
        const countries = (data.countries || []);
        const mainCountries = countries.filter((c => MAIN_COUNTRIES.includes(c.name)));

        setState({
          ...state,
          countries: countries.filter(c => !MAIN_COUNTRIES.includes(c.name)),
          mainCountries
        });
      });
  }, []);

  useEffect(() => {
    const newValues = {};
    if (get(offer, 'questions.length')) {
      const questions = {};
      offer.questions.forEach(question => {
        formik.registerField(`questions.${question.field}`, { validate: validateInput('string') });
        questions[question.field] = get(userOffer, `questions_and_answers.${question.field}`, '');
      });
      newValues.questions = questions;
    }

    if (object_attribute_metadata.length) {
      object_attribute_metadata.forEach(objectAttribute => {
        formik.registerField(objectAttribute.code, {
          validate:
            validateObjAttrValuesInput(objectAttribute.datatype, objectAttribute.is_multiple, objectAttribute.ui_element, objectAttribute.optional)
        });
        if (
          [UIElementType.CHECKBOXES, UIElementType.DROPDOWN].includes(objectAttribute.ui_element)
          && objectAttribute.is_multiple
        ) {
          let value = get(userOffer, `object_attribute_values.${objectAttribute.code}`, '');
          newValues[objectAttribute.code] = (value && value.split(',')) || '';
        } else {
          newValues[objectAttribute.code] = get(userOffer, `object_attribute_values.${objectAttribute.code}`, '');
        }

      });
    }

    let isPaymentRequired = false;
    let isPhoneRequired = false;
    let isAddressRequired = false;
    let usePrimaryAddress = false;

    if (get(offer, 'rewards.length')) {
      isPhoneRequired = offer.rewards.some(reward => reward.phone_number_required);
      isPaymentRequired = offer.rewards.some(reward => reward.payment_email_required);
      isAddressRequired = offer.rewards.some(reward => reward.address_required);

      if (isPhoneRequired) {
        formik.registerField('phone_number', { validate: validateInput('string') });
        newValues.phone_number = get(userOffer, 'questions_and_answers.phone_number') || get(user, 'phone') || '';
      }

      if (isPaymentRequired) {
        formik.registerField('paypal_email', { validate: validateInput('string') });
        newValues.paypal_email = get(userOffer, 'questions_and_answers.paypal_email') || get(user, 'paypal_email');
      }

      usePrimaryAddress = Object.keys(get(user, 'primary_address') || {}).length > 0;
      newValues.usePrimaryAddress = usePrimaryAddress;
    }

    setState({
      ...state,
      usePrimaryAddress,
      isPhoneRequired,
      isPaymentRequired,
      isAddressRequired,
      isFacebookRequired,
      isFacebookOptional,
      updatedForm: Date.now()
    });
    formik.setValues({
      address: { ...formik.values.address },
      ...newValues
    });
  }, [state.countries]);

  const onSaveInfo = (values, submit = false) => {
    setState({
      ...state,
      loading: true
    });
    const questions = {};
    for (const question in values.questions) {
      if (Object.hasOwnProperty.call(values.questions, question)) {
        const answer = values.questions[question];
        if (answer) {
          questions[question] = answer;
        }
      }
    }

    const oas = {};
    object_attribute_metadata.forEach(objectAttribute => {
      oas[objectAttribute.code] = values[objectAttribute.code];
    });

    const user_offer = {
      id: userOffer.id,
      prize_notes: {
        questions: {
          ...questions,
          ...(values.paypal_email && { paypal_email: values.paypal_email }),
          ...(values.phone_number && { phone_number: values.phone_number }),
        },
      },
      ...(oas),
      ...(!submit && { skip_validation: true }),
      ...(confirmAddressRef.current && { confirm_address: true }),
      user_offer_linked_talent_agents: latestFormikUserOfferTAChanges(),
      collective_ids: formik.values.collective_ids,
    };

    if (state.isAddressRequired) {
      if (values.usePrimaryAddress) {
        user_offer.use_primary_address = "true";
      } else {
        user_offer.address = {
          ...values.address
        };
      }
    }

    axios.patch(`/api/v1/influencers/user_offers/${userOffer.id}`, {
      user_offer
    }).then(({ data }) => {
      const userOfferTmp = { ...data.user_offer };
      const offerTmp = {
        ...offer,
        user_offer: userOfferTmp
      };
      dispatch(setOffer(offerTmp));
      confirmAddressRef.current = false;
      const addressValidationFailed = state.isAddressRequired && data.show_address_confirm === true;
      setState({
        ...state,
        loading: false,
        hasBeenSubmitted: submit && !addressValidationFailed
      });
      if (submit) {
        if (addressValidationFailed) {
          setShowAddressConfirm(true);
        } else {
          nextStep();
        }
      } else if (showConnectFacebook) {
        if (addressValidationFailed) {
          setShowAddressConfirm(true);
        } else {
          nextStep({
            connectFacebook: true,
            userOfferTmp: user_offer
          });
        }
      } else {
        onRequestClose({ savedApplication: true });
      }
    }).catch(err => {
      confirmAddressRef.current = false;
      const errors = Object.assign(get(err.response, 'data.user_offer.errors', {}));

      const mappedAddressFields = ['first_name', 'last_name', 'address', 'city', 'state', 'zip', 'phone', 'country'];
      const unmappedAddressErrors = Object.entries(errors)
        .filter(([key]) => key.startsWith('address.') && !mappedAddressFields.includes(key.replace('address.', '')))
        .flatMap(([, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]));
      const generalErrors = [
        ...(errors.current_permanent_address ? [].concat(errors.current_permanent_address) : []),
        ...unmappedAddressErrors
      ];

      formik.setStatus({
        address: {
          first_name: errors['address.first_name'],
          last_name: errors['address.last_name'],
          address: errors['address.address'],
          city: errors['address.city'],
          state: errors['address.state'],
          zip: errors['address.zip'],
          phone: errors['address.phone'],
          country: errors['address.country'],
          general: generalErrors.length ? generalErrors.join(' ') : null
        }
      });

      formik.setTouched({
        address: {
          first_name: true,
          last_name: true,
          address: true,
          city: true,
          state: true,
          zip: true,
          phone: true,
          country: true,
          general: true
        }
      });

      if (get(errors, 'address.address.easypost')) {
        const scrollTo = values.usePrimaryAddress ? '#general-error-primary-address' : '#general-error';
        scrollToHashInDiv(scrollTo, document.querySelector('.slide-pane__content'));
      } else {
        scrollToHashInDiv('#address-form', document.querySelector('.slide-pane__content'));
      }

      setState({
        ...state,
        error: err,
        loading: false
      });

    });
  };

  const handleAddressConfirmed = useCallback(() => {
    setShowAddressConfirm(false);
    confirmAddressRef.current = true;
    onSaveInfo(formik.values, true);
  }, [formik.values]);

  // Gets the latest formik values and if no values available it gets the initial values for
  // `user_offer_linked_talent_agents`
  const latestFormikUserOfferTAChanges = () => {
    if (formik.values.user_offer_linked_talent_agents) return formik.values.user_offer_linked_talent_agents;

    return formik.initialValues.user_offer_linked_talent_agents;
  };

  const renderPrimaryAddress = useCallback((address) => <div className="text-1">
    <div>{address.first_name} {address.last_name}</div>
    <div>{address.address}</div>
    {address.address2 && <div>{address.address2}</div>}
    <div>{address.city}, {address.state}</div>
    <div>{address.zip}</div>
    <div>{address.country}</div>
    <hr className='my-6' />
  </div>, []);

  const renderPrimaryAddressFields = useCallback(() => {
    const fields = ADDRESS_FIELDS.map((field) => {
      const isTouched = formik.touched.address && formik.touched.address[field];
      const hasStatus = get(formik.status, `address.${field}`);
      const hasError = !!(formik.errors.address && formik.errors.address[field]) || !!hasStatus;
      const errorMessage = (formik.errors.address && formik.errors.address[field]) || hasStatus;
      return (
        <Form.Group key={field} className="mt-3">
          <Form.Label>
            {t(`influencers.pages.profile.general.${field}`)}
          </Form.Label>
          <FormControl
            type="text"
            placeholder={t(`influencers.pages.profile.general.${field}`)}
            name={`address.${field}`}
            onChange={(e) => {
              hasStatus && set(formik.status, `address.${field}`, '');
              formik.handleChange(e);
            }}
            value={formik.values.address[field]}
            error={isTouched && hasError}
            onBlur={formik.handleBlur}
            disabled={state.loading || state.hasBeenSubmitted}
          />
          {isTouched && hasError ?
            <Form.Text className="text-muted danger-text">
              {errorMessage}
            </Form.Text> : null}
        </Form.Group>
      );
    });

    return (
      <div id="address-form">
        {fields}
        <Form.Group id="general-error">
          <Form.Label>
            {t(`influencers.pages.profile.general.country`)}
          </Form.Label>
          <Select
            className="mb-2"
            placeholder={t(`influencers.pages.profile.general.country`)}
            name={`address.country`}
            value={formik.values.address.country}
            error={Boolean(
              (get(formik, 'touched.address.country') && get(formik, 'errors.address.country'))
              || get(formik.status, 'address.country')
            )}
            onChange={(e) => {
              get(formik.status, 'address.country') && set(formik.status, 'address.country', '');
              formik.handleChange(e);
            }}
            disabled={state.loading || state.hasBeenSubmitted}
            onBlur={formik.handleBlur}
            options={
              [
                ...state.mainCountries,
                ...state.countries
              ].map(country => ({
                label: country.name,
                value: `${country.name}`
              }))
            }
          />
          {((get(formik, 'touched.address.country') && get(formik, 'errors.address.country'))
            || get(formik.status, 'address.country')) ?
            <Form.Text className="text-muted danger-text">
              {get(formik, 'errors.address.country') || get(formik.status, 'address.country')}
            </Form.Text> : null}
        </Form.Group>
        {!isEmpty(get(formik.status, 'address.general')) &&
          <Form.Text className="text-muted danger-text">
            {get(formik.status, 'address.general')}
          </Form.Text>
        }
        <hr className='my-6' />
      </div >
    );
  }, [formik]);

  const renderObjectAttributes = useCallback(() => {
    if (!object_attribute_metadata.length) return null;

    return (<React.Fragment>
      {
        get(formik, 'values.questions.length') !== 0
          ? null
          : <React.Fragment>
            <hr className='my-6' />
            <h4 className='mb-4'>
              {t('influencers.common.questions')}
            </h4>
          </React.Fragment>
      }
      {
        object_attribute_metadata.map(objectAttribute => {
          const code = objectAttribute.code;
          const isTouched = formik.touched && formik.touched[code];
          const hasError = formik.errors && formik.errors[code];

          return (<Row key={code}>
            <Col sm={12} key={code}>
              <ObjectAttribute
                fieldData={objectAttribute}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values[code]}
                name={code}
                error={isTouched && hasError} />
            </Col>
          </Row>);
        })
      }
    </React.Fragment>);
  }, [formik]);

  const renderQuestions = useCallback(() => {
    if (!offer.questions || !offer.questions.length) return null;
    if (!formik.values.questions) return;

    return (<React.Fragment>
      <hr className='my-6' />
      <h4 className='mb-4'>
        {t('influencers.common.questions')}
      </h4>
      {
        offer.questions.map(question => {
          const field = question.field;

          const isTouched = formik.touched.questions && formik.touched.questions[field];
          const hasError = formik.errors.questions && formik.errors.questions[field];
          return (<Form.Group key={question.field}>
            <Form.Label>
              {question.question}
            </Form.Label>
            <FormControl
              type="text"
              name={`questions.${field}`}
              placeholder={t(`influencers.pages.campaign.apply_side_pane.insert_your_answer`)}
              error={!!(isTouched && hasError)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.questions[field]}
              disabled={state.loading || state.hasBeenSubmitted}
            />
            {isTouched && hasError ?
              <Form.Text className="text-muted danger-text">
                {hasError}
              </Form.Text> : null}
          </Form.Group>);
        })
      }
    </React.Fragment>);
  }, [formik]);

  const renderPhoneNumber = useCallback(() => (
    <React.Fragment>
      <h4 className='mb-4'>
        {t('influencers.common.phone_number')}
      </h4>
      <Form.Group>
        <Form.Label>
          {t('influencers.common.phone_number')}
        </Form.Label>
        <FormControl
          type="text"
          placeholder={t('influencers.common.phone_number')}
          name="phone_number"
          onChange={formik.handleChange}
          value={formik.values.phone_number}
          error={!!(formik.touched.phone_number && formik.errors.phone_number)}
          onBlur={formik.handleBlur}
          disabled={state.loading || state.hasBeenSubmitted}
        />
        {formik.touched.phone_number && formik.errors.phone_number ?
          <Form.Text className="text-muted danger-text">
            {formik.errors.phone_number}
          </Form.Text> : null}
      </Form.Group>
    </React.Fragment>
  ), [formik]);

  const renderPaypalAddress = useCallback(() => (
    <React.Fragment>
      <hr className='my-6' />
      <h4 className='mb-4'>
        {t('influencers.common.paypal_email')}
      </h4>
      <Form.Group>
        <Form.Label>
          {t('influencers.common.email_address')}
        </Form.Label>
        <FormControl
          type="email"
          placeholder={t('influencers.common.email_address')}
          name="paypal_email"
          data-cy="paypal_email"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={!!(formik.touched.paypal_email && formik.errors.paypal_email)}
          value={formik.values.paypal_email}
          disabled={state.loading || state.hasBeenSubmitted}
        />
        {formik.touched.paypal_email && formik.errors.paypal_email ?
          <Form.Text className="text-muted danger-text">
            {formik.errors.paypal_email}
          </Form.Text> : null}
      </Form.Group>
    </React.Fragment>
  ), [formik]);

  const renderUserTalentAgents = useCallback(() => {
    if (isEmpty(talentAgents)) return null;

    // Mark checkbox
    const isChecked = (taID) => latestFormikUserOfferTAChanges().includes(taID);

    // Handle formik talent agent value changes
    const handleCheckboxChange = (e) => {
      const { value, checked } = e.target;
      const userOfferTAgentValues = latestFormikUserOfferTAChanges();

      if (checked) {
        formik.setFieldValue(
          'user_offer_linked_talent_agents',
          [...userOfferTAgentValues, parseInt(value, 10)]
        );
      } else {
        formik.setFieldValue(
          'user_offer_linked_talent_agents',
          [...userOfferTAgentValues.filter(id => id !== parseInt(value, 10))]
        );
      }
    };

    const talentAgentCheckboxes = talentAgents.map(talentAgent => (
      <Form.Check
        key={`talent-agent-${talentAgent.id}`}
        type='checkbox'
        label={`${talentAgent.fullname}: ${talentAgent.email}`}
        id={`talent-agent-${talentAgent.id}`}
        className='text-medium-3 my-2'
        value={talentAgent.id}
        checked={isChecked(talentAgent.id)}
        onChange={handleCheckboxChange}
      />
    ));

    return (
      <React.Fragment>
        <h4 className='mb-4 mt-5'>
          {t('influencers.common.talent_agents.title')}
        </h4>
        <Form.Group>
          <Form.Label className='font-italic'>
            {t('influencers.common.talent_agents.description')}
          </Form.Label>
          {talentAgentCheckboxes}
        </Form.Group>
      </React.Fragment>
    );
  }, [formik]);

  const renderCollectives = useCallback(() => {
    if (!offer.collectives?.length) return null;

    const handleChange = (e) => {
      const id = parseInt(e.target.value);
      const current = formik.values.collective_ids || [];
      formik.setFieldValue(
        'collective_ids',
        e.target.checked ? [...current, id] : current.filter(cid => cid !== id)
      );
    };

    return (
      <React.Fragment>
        <hr className='my-4' />
        <h4 className='mb-4'>{t('influencers.common.collectives.title')}</h4>
        <Form.Group>
          <Form.Label className='font-italic'>
            {t('influencers.common.collectives.description')}
          </Form.Label>
          {offer.collectives.map(collective => (
            <Form.Check
              key={`collective-${collective.id}`}
              type='checkbox'
              label={collective.name}
              id={`collective-${collective.id}`}
              className='text-medium-3 my-2'
              value={collective.id}
              checked={(formik.values.collective_ids || []).includes(collective.id)}
              onChange={handleChange}
            />
          ))}
        </Form.Group>
      </React.Fragment>
    );
  }, [formik, offer.collectives]);

  if (!user) return null;

  return (<React.Fragment>
    <h3 className='mb-4' >
      {t('influencers.pages.campaign.apply_side_pane.required_information')}
    </h3>
    {state.isAddressRequired &&
      <h4 className='mb-4'>
        {`${t('influencers.pages.campaign.apply_side_pane.shipping_address_for')}`}
        <br />
        {formatDate(offer.timelines.submit_content_draft.start)} - {formatDate(offer.timelines.submit_content_draft.start, { addDays: 7 })}
      </h4>}
    <Form onSubmit={formik.handleSubmit}>
      {state.isAddressRequired && user.address && (
        <Fragment>
          <InputGroup id="general-error-primary-address">
            <Switch
              name="usePrimaryAddress"
              onChange={(checked) => formik.setFieldValue('usePrimaryAddress', checked)}
              checked={formik.values.usePrimaryAddress}
              disabled={state.loading || state.hasBeenSubmitted}
            />
            <FormLabel>
              {t('influencers.pages.campaign.apply_side_pane.use_primary_address')}
            </FormLabel>
          </InputGroup>
          {formik.values.usePrimaryAddress && !isEmpty(get(formik.status, 'address.general')) &&
            <Form.Text className="text-muted danger-text mb-3">
              {get(formik.status, 'address.general')}
            </Form.Text>
          }
        </Fragment>
      )}

      {state.isAddressRequired &&
        (formik.values.usePrimaryAddress && user.address
          ? renderPrimaryAddress(user.address)
          : renderPrimaryAddressFields()
        )
      }

      {state.isPhoneRequired ? renderPhoneNumber() : null}
      {state.isPaymentRequired ? renderPaypalAddress() : null}
      {formik.values &&
        formik.values.questions &&
        Object.keys(formik.values.questions).length
        ? renderQuestions()
        : null
      }

      {formik.values &&
        object_attribute_metadata.length
        ? renderObjectAttributes()
        : null
      }

      {renderCollectives()}

      { /** Render Talent agents data */}
      {renderUserTalentAgents()}

      <Modal show={showAddressConfirm} onHide={() => setShowAddressConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('influencers.pages.campaign.apply_side_pane.confirm_shipping_address')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('influencers.pages.campaign.apply_side_pane.confirm_address_message')}</p>
          <div className="p-3 bg-light rounded text-1">
            {formik.values.usePrimaryAddress && user.address
              ? <React.Fragment>
                <div>{user.address.first_name} {user.address.last_name}</div>
                <div>{user.address.address}</div>
                {user.address.address2 && <div>{user.address.address2}</div>}
                <div>{user.address.city}, {user.address.state} {user.address.zip}</div>
                <div>{user.address.country}</div>
              </React.Fragment>
              : <React.Fragment>
                <div>{formik.values.address.first_name} {formik.values.address.last_name}</div>
                <div>{formik.values.address.address}</div>
                {formik.values.address.address_2 && <div>{formik.values.address.address_2}</div>}
                <div>{formik.values.address.city}, {formik.values.address.state} {formik.values.address.zip}</div>
                <div>{formik.values.address.country}</div>
              </React.Fragment>
            }
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={() => setShowAddressConfirm(false)}>
            {t('influencers.pages.campaign.apply_side_pane.edit_address')}
          </Button>
          <Button variant="primary" type="button" onClick={handleAddressConfirmed}>
            {t('influencers.pages.campaign.apply_side_pane.address_looks_correct')}
          </Button>
        </Modal.Footer>
      </Modal>

      <hr className='my-6' />
      <Row className='justify-content-end'>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 4
          }} sm={{
            span: "auto",
            order: 1
          }}>
          <Button
            variant="tertiary"
            type="button"
            onClick={() => onRequestClose()}
            disabled={state.loading}>
            {t('influencers.common.cancel')}
          </Button>
        </Col>
        <Col
          className="px-sm-2 mb-2"
          xs={{
            span: "auto",
            order: 1
          }}
          sm={{
            span: "auto",
            order: 2
          }}
        >
          <Button
            variant="secondary"
            type="button"
            onClick={prevStep}
            disabled={state.loading}>
            {t('influencers.common.back')}
          </Button>
        </Col>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 2
          }}
          sm={{
            span: "auto",
            order: 3
          }}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => onSaveInfo(formik.values)}
            disabled={state.loading}>
            {t('influencers.common.save')}
          </Button>
        </Col>
        <Col
          className="px-sm-2"
          xs={{
            span: "auto",
            order: 3
          }} sm={{
            span: "auto",
            order: 4
          }}>
          <Button
            idButton="offer-application-required-info"
            lastStep={lastStep()}
            variant="primary"
            type="submit"
            disabled={!state.hasBeenSubmitted && (state.loading || !formik.isValid)}>
            {state.loading
              ? <Spinner animation="border" size="sm" />
              : t('influencers.common.next')
            }
          </Button>
        </Col>
      </Row>

    </Form>
  </React.Fragment>);
};

RequiredInformation.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  lastStep: PropTypes.func.isRequired
};

export default React.memo(RequiredInformation);
